import {
  useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties,
} from "react";
import { useTour } from "./TourProvider";
import { CHAPTERS } from "./chapters";
import {
  resolveTarget, findAnchor, isUsableAnchor, ANCHOR_WAIT_MS,
} from "./resolveTarget";
import { cutoutRect, placeCard, centerCard, type Rect } from "./placement";
import type { TourContext } from "./types";

export const TOUR_Z_INDEX = 10000;
export const CARD_WIDTH = 440;
const DIM = "rgba(0,0,0,0.62)";
/** Below this the card stops floating and docks as a full-width bottom sheet. */
const SHEET_MAX_WIDTH = 640;
/** A hands-on card with nothing to anchor to waits in the bottom-right
 *  corner, clear of the toolbar (bottom of the video panel) and the map rail
 *  (top-right); it moves beside the target as soon as one exists. */
const DOCK_MARGIN = 16;
const RING_PAD = 6;
const STYLE_ID = "vmc-tour-styles";
const DIM_MASK_ID = "vmc-tour-dim-mask";
const RING_KEYFRAMES =
  `@keyframes vmc-tour-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }`;

/** Both the resolution and the measured placement are stamped with the step
 *  they belong to. A stamp that does not match the step being rendered is
 *  stale — from the previous step, or from a chapter that has already ended —
 *  and must never reach the DOM or the measuring code. */
type Resolution = { key: string } & (
  | { kind: "pending" }
  | { kind: "anchored"; el: HTMLElement }
  | { kind: "centered" }
);

interface Placed {
  key: string;
  target: Rect | null;
  pos: { top: number; left: number } | null;
}

const btn: CSSProperties = {
  minHeight: 38, padding: "7px 16px", borderRadius: 5, fontSize: 14, cursor: "pointer",
  background: "#252525", color: "#ddd", border: "1px solid #3a3a3a", fontFamily: "system-ui",
};
const primaryBtn: CSSProperties = {
  ...btn,
  background: "#4cf", color: "#031318", border: "1px solid #4cf", fontWeight: 600,
};

/** One stylesheet for the ring's pulse — inline styles cannot express
 *  keyframes. Idempotent, same pattern as the frames page's menu styles. */
function ensureStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = RING_KEYFRAMES;
  document.head.appendChild(el);
}

export function TourOverlay(): JSX.Element | null {
  const { state, next, back, skip, dropStep, contextOf, contextVersion } = useTour();
  const step = state.active ? (state.steps[state.stepIndex] ?? null) : null;
  const stepKey = state.active ? `${state.chapterId}:${step?.id}` : "";
  const chapterId = state.active ? state.chapterId : null;
  const isDo = step?.kind === "do";
  const [res, setRes] = useState<Resolution>({ key: "", kind: "pending" });
  const [placed, setPlaced] = useState<Placed>({ key: "", target: null, pos: null });
  // The last measured target rect, for the live re-anchor check to compare
  // against without re-rendering.
  const placedRef = useRef<Rect | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  // The anchor already brought into view, so a re-measure never re-scrolls.
  const scrolledFor = useRef<{ key: string; el: HTMLElement } | null>(null);
  const [narrow, setNarrow] = useState(() => window.innerWidth < SHEET_MAX_WIDTH);
  const [tick, setTick] = useState(0); // bumps to force a re-measure
  // Hands-on completion, latched per step for the chapter's lifetime, plus the
  // context each do-step snapshotted when it first became current — one entry
  // per step, so Back onto a step re-uses the baseline it already took rather
  // than re-snapshotting a page the reviewer has since changed.
  const doneRef = useRef(new Set<string>());
  const baselinesRef = useRef(new Map<string, TourContext>());
  // Step keys whose predicate has already thrown, so a broken one warns once
  // rather than on every re-evaluation.
  const warnedRef = useRef(new Set<string>());
  // The latch lives in a ref so Back onto a completed step shows the success
  // state without re-evaluating; this counter is what turns a latch into a
  // render.
  const [, bumpDone] = useState(0);
  const isDone = isDo && doneRef.current.has(stepKey);

  // A stale stamp reads as "pending", so a step change instantly forgets the
  // previous anchor. Memoised so the substitute keeps a stable identity across
  // re-renders; the layout effect below depends on it.
  const current = useMemo<Resolution>(
    () => (res.key === stepKey ? res : { key: stepKey, kind: "pending" }),
    [res, stepKey],
  );
  const targetRect = placed.key === stepKey ? placed.target : null;
  const cardPos = placed.key === stepKey ? placed.pos : null;
  placedRef.current = targetRect;

  useEffect(() => { ensureStyles(); }, []);

  // Resolve the step's anchor. A layout effect, because both synchronous
  // outcomes below have to land before the step's first paint: an anchor that
  // is already in the DOM must never flash centered, and a role-gated step the
  // reviewer does not have must never be shown at all.
  //
  // A hands-on step only ever checks synchronously: the ring is a courtesy,
  // never a gate, so there is nothing to hunt for and no fallback to fall to.
  useLayoutEffect(() => {
    if (!state.active || step === null) return;
    const key = stepKey;
    if (step.target === null) { setRes({ key, kind: "centered" }); return; }
    const now = findAnchor(step.target, step.pick);
    if (now !== null) { setRes({ key, kind: "anchored", el: now }); return; }
    if (step.kind === "do") { setRes({ key, kind: "centered" }); return; }
    // A "skip" step is checked synchronously and only once: there is nothing to
    // wait for, because the control is gated on a role or a flight property
    // that will not change while the card is up. Dropping it here keeps the
    // "Finding the control…" card for a feature the reviewer does not have off
    // the screen entirely.
    if ((step.fallback ?? "describe") === "skip") { dropStep(step.id); return; }
    // "describe" steps hunt: the control may still be mounting.
    const ac = new AbortController();
    setRes({ key, kind: "pending" });
    void resolveTarget(step.target, {
      timeoutMs: step.waitMs ?? ANCHOR_WAIT_MS, signal: ac.signal,
    }).then((el) => {
      if (ac.signal.aborted) return;
      setRes(el !== null ? { key, kind: "anchored", el } : { key, kind: "centered" });
    });
    return () => ac.abort();
    // stepKey captures chapter+step identity; `step` itself is derived from state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, state.active, dropStep]);

  // Focus the title on every explain step so keys land in the card and screen
  // readers announce the change. A hands-on step must NOT take focus: the page
  // is the lesson — "Name it" relies on the viewer's own auto-focused name
  // field, and the map's arrow-key pan only works while the map is focused.
  // The card's content region is an aria-live region instead, so the step
  // change is still announced.
  useEffect(() => {
    if (!state.active) return;
    if (!isDo) { titleRef.current?.focus(); return; }
    // Coming from an explain step, focus is sitting on the card's title.
    // Leaving it there would swallow the very keys the lesson needs, so hand
    // it back to the page. Focus the reviewer moved themselves is left alone.
    if (document.activeElement === titleRef.current) titleRef.current?.blur();
  }, [stepKey, state.active, isDo]);

  // Per-run state: the scroll guard and the hands-on latches both reset when
  // the tour deactivates, never mid-chapter. (A replay revisits the same step
  // keys and the same nodes, and the page may have been scrolled away — or the
  // action undone — in between, so a finished run must not speak for the next.)
  useEffect(() => {
    if (state.active) return;
    scrolledFor.current = null;
    doneRef.current.clear();
    baselinesRef.current.clear();
    warnedRef.current.clear();
  }, [state.active]);

  // Hands-on evaluation. Baseline once per step; re-check on every accepted
  // context change (contextVersion) and on DOM mutations under the viewer
  // host, one check per frame. The first true latches.
  useEffect(() => {
    if (!state.active || step === null || step.kind !== "do" || chapterId === null) return;
    const done = step.done;
    if (done === undefined || doneRef.current.has(stepKey)) return;
    const read = (): TourContext => contextOf(chapterId) ?? { annotations: null, document };
    let baseline = baselinesRef.current.get(stepKey);
    if (baseline === undefined) {
      baseline = read();
      baselinesRef.current.set(stepKey, baseline);
    }
    const snapshot = baseline;
    let raf = 0;
    const evaluate = (): void => {
      if (doneRef.current.has(stepKey)) return;
      let ok = false;
      try {
        ok = done(read(), snapshot);
      } catch (err) {
        // A predicate is chapter data reading a page the tour does not own.
        // A broken one costs its own step (the reviewer can still Skip this
        // step) and never the chapter, so it is swallowed after one warning.
        if (!warnedRef.current.has(stepKey)) {
          warnedRef.current.add(stepKey);
          console.warn(`[tour] "${stepKey}" predicate threw; step left incomplete`, err);
        }
        return;
      }
      if (!ok) return;
      doneRef.current.add(stepKey);
      bumpDone((t) => t + 1);
    };
    const schedule = (): void => {
      if (raf !== 0) return;
      raf = requestAnimationFrame(() => { raf = 0; evaluate(); });
    };
    evaluate();
    const host = document.querySelector('[data-testid="viewer-host"]') ?? document.body;
    const mo = new MutationObserver(schedule);
    // Only the zoom badge's `hidden` attribute drives a predicate from the
    // DOM; watching every attribute of the whole viewer re-evaluates on each
    // map pan and video frame for nothing.
    mo.observe(host, {
      childList: true, subtree: true, attributes: true, attributeFilter: ["hidden"],
    });
    return () => { mo.disconnect(); if (raf !== 0) cancelAnimationFrame(raf); };
    // `step` is derived from state and keyed by stepKey. `isDone` is here so
    // the latch tears the observer down: without it the rAF-throttled re-check
    // would keep running over the viewer's DOM for a step already complete.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, state.active, chapterId, contextOf, contextVersion, isDone]);

  // Capture-phase keys: only while an explain step owns the page, so the
  // viewer's page-global shortcuts (Space, J, L, M, F, arrows…) never fire
  // underneath. A hands-on step leaves the keyboard to the viewer (Space
  // plays, Esc cancels pin mode).
  useEffect(() => {
    // Same condition the render bails on: the tour must never own the
    // keyboard while it is showing nothing.
    if (!state.active || step === null || isDo) return;
    const onKey = (e: KeyboardEvent): void => {
      // Reload, find and copy stay with the browser: no page shortcut of ours
      // uses Cmd/Ctrl, and a reviewer must be able to copy the card's own
      // shortcut table. (Alt is not browser-critical, so it falls through.)
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      e.stopPropagation();
      const card = cardRef.current;
      const active = document.activeElement;
      const buttons = card !== null
        ? Array.from(card.querySelectorAll("button")).filter((b) => !b.disabled)
        : [];
      const focusedBtn = active instanceof HTMLButtonElement && card?.contains(active) === true
        ? active
        : null;
      if ((e.key === "Enter" || e.key === " ") && focusedBtn !== null) {
        focusedBtn.click();
        return;
      }
      switch (e.key) {
        case "Escape": skip(); return;
        // Next stays live during an anchor hunt, so Enter/ArrowRight do too.
        case "ArrowRight": case "Enter": next(); return;
        case "ArrowLeft": back(); return;
        case "Tab": {
          if (buttons.length === 0) return;
          const i = focusedBtn !== null ? buttons.indexOf(focusedBtn) : -1;
          const dir = e.shiftKey ? -1 : 1;
          const n = buttons.length;
          const from = i === -1 ? (dir === 1 ? -1 : 0) : i;
          buttons[(from + dir + n) % n]?.focus();
          return;
        }
        default: return;
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // stepKey changes with `step`, which is derived from state, so the guard
    // above cannot go stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.active, stepKey, isDo, next, back, skip]);

  // Re-measure on resize, any scroll, a folded section, and target resize;
  // one measure per frame.
  useEffect(() => {
    if (!state.active) return;
    const anchored = current.kind === "anchored" ? current.el : null;
    const target = step?.target ?? null;
    const pick = step?.pick;
    let raf = 0;
    const remeasure = (): void => {
      setNarrow(window.innerWidth < SHEET_MAX_WIDTH);
      // An anchor can vanish while its own step is still on screen — the
      // save-state pill unmounts when it goes idle, the Install button removes
      // itself after use, a Flights section gets folded away. Move to the
      // next usable match if there is one (another section's list), else
      // fall back to the centered card (and no ring) rather than measure a
      // node that is no longer in the page.
      if (anchored !== null && !isUsableAnchor(anchored)) {
        const el = target !== null ? findAnchor(target, pick) : null;
        setRes(el !== null ? { key: stepKey, kind: "anchored", el } : { key: stepKey, kind: "centered" });
        return;
      }
      setTick((t) => t + 1);
    };
    const schedule = (): void => {
      if (raf !== 0) return;
      raf = requestAnimationFrame(() => { raf = 0; remeasure(); });
    };
    window.addEventListener("resize", remeasure);
    document.addEventListener("scroll", schedule, true);
    // <details> fires `toggle` without bubbling; capture still sees it.
    document.addEventListener("toggle", schedule, true);
    let ro: ResizeObserver | null = null;
    if (anchored !== null && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(schedule);
      ro.observe(anchored);
    }
    return () => {
      window.removeEventListener("resize", remeasure);
      document.removeEventListener("scroll", schedule, true);
      document.removeEventListener("toggle", schedule, true);
      ro?.disconnect();
      if (raf !== 0) cancelAnimationFrame(raf);
    };
    // `step` is derived from state and keyed by stepKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.active, current, stepKey]);

  // Measure and place. Runs after every render of an active step so the card's
  // own size (which depends on its text) is known before positioning. A
  // hands-on card is placed beside its anchor exactly like an explain card;
  // without an anchor it docks in a corner and needs no position.
  useLayoutEffect(() => {
    if (!state.active) return;
    // Nothing listens for resizes while the tour is idle, so the sheet flag can
    // be stale by the time a chapter starts. Re-read it before this paint.
    setNarrow(window.innerWidth < SHEET_MAX_WIDTH);
    const vp = { width: window.innerWidth, height: window.innerHeight };
    const card = cardRef.current;
    const boxRect = card?.getBoundingClientRect();
    const size = boxRect !== undefined
      ? { width: boxRect.width || CARD_WIDTH, height: boxRect.height || 200 }
      : { width: CARD_WIDTH, height: 200 };
    if (current.kind === "anchored") {
      // Bring the target into view once per anchor. A re-measure (resize,
      // scroll, target resize) must not yank the page back under the reviewer.
      const done = scrolledFor.current;
      if (done === null || done.key !== stepKey || done.el !== current.el) {
        scrolledFor.current = { key: stepKey, el: current.el };
        current.el.scrollIntoView?.({ block: "center", inline: "nearest" });
      }
      const r = current.el.getBoundingClientRect();
      const t = { top: r.top, left: r.left, width: r.width, height: r.height };
      setPlaced({
        key: stepKey,
        target: t,
        pos: placeCard(t, size, vp, step?.placement ?? "auto"),
      });
    } else {
      setPlaced({ key: stepKey, target: null, pos: isDo ? null : centerCard(size, vp) });
    }
  }, [current, stepKey, state.active, step?.placement, tick, isDo]);

  // Hands-on steps anchor live. The thing a lesson points at can appear,
  // vanish or move while the step is up — a pin's card opens on the very
  // click the lesson asks for, and the map pans under a popup — so re-check
  // the anchor when popups or markers change and when the map pane moves,
  // one check per frame. Explain steps keep their one-shot resolution.
  useEffect(() => {
    if (!state.active || step === null || step.kind !== "do" || step.target === null) return;
    const target = step.target;
    const pick = step.pick;
    const key = stepKey;
    const anchored = current.kind === "anchored" ? current.el : null;
    let raf = 0;
    const check = (): void => {
      const el = findAnchor(target, pick);
      if (el !== anchored) {
        setRes(el !== null ? { key, kind: "anchored", el } : { key, kind: "centered" });
        return;
      }
      if (el === null) return;
      const r = el.getBoundingClientRect();
      const last = placedRef.current;
      const moved = last === null || last.top !== r.top || last.left !== r.left
        || last.width !== r.width || last.height !== r.height;
      if (moved) setTick((t) => t + 1);
    };
    const schedule = (): void => {
      if (raf !== 0) return;
      raf = requestAnimationFrame(() => { raf = 0; check(); });
    };
    // Popups and markers live in Leaflet's panes; before the map exists (or in
    // a test), fall back to the viewer host. Never the whole document: the
    // viewer rewrites its info bar every frame.
    const panes = [".leaflet-popup-pane", ".leaflet-marker-pane"]
      .map((sel) => document.querySelector(sel))
      .filter((el): el is Element => el !== null);
    const roots = panes.length > 0
      ? panes
      : [document.querySelector('[data-testid="viewer-host"]') ?? document.body];
    const observers = roots.map((root) => {
      const mo = new MutationObserver(schedule);
      mo.observe(root, { childList: true, subtree: true });
      return mo;
    });
    const pane = document.querySelector(".leaflet-map-pane");
    if (pane !== null) {
      const mo = new MutationObserver(schedule);
      mo.observe(pane, { attributes: true, attributeFilter: ["style"] });
      observers.push(mo);
    }
    return () => {
      for (const mo of observers) mo.disconnect();
      if (raf !== 0) cancelAnimationFrame(raf);
    };
    // `step` is derived from state and keyed by stepKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.active, stepKey, current]);

  if (!state.active || step === null) return null;

  const chapter = CHAPTERS[state.chapterId];
  const isLast = state.stepIndex === state.steps.length - 1;
  const heading = (step.section !== undefined
    ? `${chapter.title} · ${step.section}`
    : chapter.title) + (isDo && !isDone ? " · Try it" : "");
  const handoffLine = step.handoff ?? null;
  // `targetRect` is already null unless it was measured for THIS step, so a
  // step change drops the previous spotlight in the very same commit.
  const cut = !isDo && targetRect !== null ? cutoutRect(targetRect) : null;
  const ring = isDo && targetRect !== null
    ? {
        top: targetRect.top - RING_PAD, left: targetRect.left - RING_PAD,
        width: targetRect.width + 2 * RING_PAD, height: targetRect.height + 2 * RING_PAD,
      }
    : null;

  // A hands-on step with nothing to anchor to (its card is closed, its pin not
  // yet dropped) waits in the bottom-right corner, clear of the toolbar and
  // the map rail, and jumps beside the target as soon as it exists.
  const docked = isDo && current.kind !== "anchored";
  const cardPlacement: CSSProperties = narrow
    ? {
        // Below SHEET_MAX_WIDTH there is no room to float beside anything:
        // the card docks to the bottom edge as a full-width sheet.
        position: "absolute", bottom: 0, left: 0, width: "100%",
        borderRadius: "10px 10px 0 0", maxHeight: "60vh", overflowY: "auto",
      }
    : docked
      ? {
          position: "fixed", bottom: DOCK_MARGIN, right: DOCK_MARGIN,
          width: CARD_WIDTH, maxWidth: "92vw", borderRadius: 8,
          maxHeight: `calc(100vh - ${2 * DOCK_MARGIN}px)`, overflowY: "auto",
        }
      : {
          position: "absolute", top: cardPos?.top ?? 0, left: cardPos?.left ?? 0,
          width: CARD_WIDTH, maxWidth: "92vw", borderRadius: 8,
          // The page itself cannot scroll (html,body{overflow:hidden}), so a
          // card taller than the viewport would put its buttons out of reach.
          // Scroll inside the card instead.
          maxHeight: "calc(100vh - 24px)", overflowY: "auto",
        };

  return (
    <div
      data-testid="tour-overlay"
      // In interactive mode the layer itself must not swallow the page's
      // pointer events; only the card does.
      style={{
        position: "fixed", inset: 0, zIndex: TOUR_Z_INDEX,
        pointerEvents: isDo ? "none" : "auto",
      }}
    >
      {/* The backdrop intercepts every pointer event; nothing beneath is
          interactive. A hands-on step has none: the page is the lesson. */}
      {!isDo && (
        <div
          data-testid="tour-backdrop"
          style={{
            position: "absolute", inset: 0,
            background: cut !== null ? "transparent" : DIM,
            touchAction: "none",
          }}
        />
      )}
      {cut !== null && (
        <>
          {/* The dim is a full-viewport rect with a rounded hole masked out.
              A box-shadow spread used to do this job, but a spread shadow's
              corners are rounded by radius + spread, so with the anchor in
              one corner of a wide window the opposite corner sat outside
              the shadow and showed through (Safari). A mask is exact. */}
          <svg
            data-testid="tour-dim"
            aria-hidden="true"
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              pointerEvents: "none",
            }}
          >
            <defs>
              <mask
                id={DIM_MASK_ID}
                maskUnits="userSpaceOnUse"
                x="0" y="0" width="100%" height="100%"
              >
                <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
                <rect
                  x={cut.left} y={cut.top} width={cut.width} height={cut.height}
                  rx={6} ry={6} fill="#000"
                />
              </mask>
            </defs>
            <rect
              x="0" y="0" width="100%" height="100%" fill={DIM}
              mask={`url(#${DIM_MASK_ID})`}
            />
          </svg>
          <div
            data-testid="tour-cutout"
            style={{
              position: "absolute",
              top: cut.top, left: cut.left, width: cut.width, height: cut.height,
              borderRadius: 6,
              pointerEvents: "none",
            }}
          />
        </>
      )}
      {ring !== null && (
        <div
          data-testid="tour-ring"
          style={{
            position: "absolute",
            top: ring.top, left: ring.left, width: ring.width, height: ring.height,
            border: "3px solid #4cf", borderRadius: 8, boxSizing: "border-box",
            pointerEvents: "none",
            animation: "vmc-tour-pulse 1.6s ease-in-out infinite",
          }}
        />
      )}
      <div
        ref={cardRef}
        data-testid="tour-card"
        role="dialog"
        aria-modal={isDo ? undefined : "true"}
        aria-labelledby="tour-step-title"
        style={{
          ...cardPlacement,
          pointerEvents: "auto",
          boxSizing: "border-box",
          background: "#1b1b1b", color: "#eee", border: "1px solid #333",
          padding: "20px 22px", fontFamily: "system-ui", fontSize: 15, lineHeight: 1.55,
          boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
          visibility: narrow || docked || cardPos !== null ? "visible" : "hidden",
        }}
      >
        {/* An explain step focuses its title, which is announced already; a
            hands-on step must not take focus, so its card announces itself. */}
        <div aria-live={isDo ? "polite" : undefined}>
          <div
            style={{
              display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9ab",
              letterSpacing: 0.4, textTransform: "uppercase",
            }}
          >
            <span>{heading}</span>
            <span>{`${state.stepIndex + 1} of ${state.steps.length}`}</span>
          </div>
          <h3
            ref={titleRef}
            id="tour-step-title"
            tabIndex={-1}
            style={{ margin: "14px 0 8px", fontSize: 20, color: "#4cf", outline: "none" }}
          >
            {step.title}
          </h3>
          {!isDo && current.kind === "pending" && (
            <p style={{ margin: "4px 0", color: "#888", fontStyle: "italic" }}>
              Finding the control…
            </p>
          )}
          {isDone && step.success !== undefined ? (
            <div
              data-testid="tour-success"
              style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                background: "rgba(76,204,255,0.12)", borderLeft: "3px solid #4cf",
                borderRadius: 6, padding: "10px 12px", margin: "10px 0",
              }}
            >
              <span aria-hidden style={{ color: "#4cf", fontWeight: 700 }}>✓</span>
              <span>{step.success}</span>
            </div>
          ) : (
            step.body.split("\n").map((para, i) => (
              <p key={i} style={{ margin: "10px 0" }}>{para}</p>
            ))
          )}
          {step.keys !== undefined && !isDone && (
            <table style={{ borderCollapse: "collapse", margin: "10px 0", fontSize: 14 }}>
              <tbody>
                {step.keys.map(([k, v]) => (
                  <tr key={k}>
                    <td
                      style={{
                        padding: "3px 12px 3px 0", whiteSpace: "nowrap", color: "#4cf",
                        fontFamily: "ui-monospace, monospace",
                      }}
                    >
                      {k}
                    </td>
                    <td style={{ padding: "3px 0" }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {handoffLine !== null && handoffLine !== "" && (
            <p style={{ margin: "12px 0 4px", color: "#4cf", fontWeight: 600 }}>
              {handoffLine}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center" }}>
          <button type="button" style={btn} onClick={skip}>Skip tour</button>
          <span style={{ flex: 1 }} />
          <button type="button" style={btn} disabled={state.stepIndex === 0} onClick={back}>
            Back
          </button>
          {/* Never disabled while an anchor is hunted: clicking abandons the
              hunt through this effect's cleanup rather than stalling the
              reviewer for ANCHOR_WAIT_MS on a control that isn't there. */}
          {isDo && !isDone ? (
            <button type="button" style={primaryBtn} onClick={next}>Skip this step</button>
          ) : (
            <button type="button" style={primaryBtn} onClick={next}>
              {isLast ? "Done" : "Next"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
